import { Link } from 'react-router-dom'
import { Sparkles, ArrowLeft, Shield, FileText, AlertTriangle, Scale, Ban, Coins, Mail } from 'lucide-react'
import { useAuthStore } from '../store/authStore'

export default function Terms() {
  const { user } = useAuthStore()
  return (
    <div>

      <div className="max-w-3xl mx-auto px-4 pt-4 pb-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link
            to="/"
            className="p-2 rounded-xl glass-hover text-gray-400 hover:text-white transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold gradient-text">Пользовательское соглашение</h1>
              <p className="text-xs text-gray-500">Последнее обновление: 18 июня 2026 г.</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="glass-card p-6 md:p-8 space-y-6 text-sm text-gray-300 leading-relaxed">
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 text-amber-300 text-xs">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <div>
                <strong>Сайт использует технологии искусственного интеллекта.</strong>
                {' '}Результаты генерации носят информационный и творческий характер.
                Администрация не гарантирует точность, безопасность или пригодность
                сгенерированного контента для любых целей.
              </div>
            </div>
          </div>

          <Section icon={<FileText className="w-5 h-5" />} title="1. Общие положения">
            <p>1.1. Настоящее Пользовательское соглашение (далее — «Соглашение») регулирует отношения
            между владельцем сайта Chat-Neuro.ru (далее — «Администрация») и любым лицом,
            использующим Сайт (далее — «Пользователь»).</p>
            <p>1.2. Использование Сайта означает безоговорочное принятие Пользователем
            всех условий Соглашения. Если Пользователь не согласен с условиями — он обязан
            немедленно прекратить использование Сайта.</p>
            <p>1.3. Администрация вправе изменять Соглашение. Новая редакция публикуется
            на Сайте и вступает в силу через 7 (семь) дней после публикации.
            Для Пользователей, имеющих активную платную подписку, существенные изменения,
            ухудшающие их положение, вступают в силу не ранее окончания оплаченного периода,
            если они не дадут отдельного согласия. Если Пользователь не согласен с
            изменениями, он обязан прекратить использование Сайта и вправе потребовать
            возврата средств за неиспользованные токены согласно разделу 5.</p>
            <p>1.4. Сайт предназначен для лиц, достигших 18 лет. Используя Сайт, Пользователь
            подтверждает, что он достиг совершеннолетия. Администрация не может гарантировать
            отсутствие доступа лиц младше 18 лет. Ответственность за предоставление доступа
            несовершеннолетним несут их законные представители.</p>
            <p>1.5. Заключая настоящее Соглашение, Пользователь даёт Администрации
            согласие на обработку персональных данных в объёме и целях,
            указанных в{' '}
            <Link to="/privacy" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2 whitespace-nowrap">
            Политике конфиденциальности</Link>,
            на срок использования Сайта. Согласие может быть отозвано путём
            удаления аккаунта.</p>
          </Section>

          <Section icon={<Ban className="w-5 h-5" />} title="2. Запрещённое использование">
            <p>2.1. Пользователю запрещается:</p>
            <p>2.1.1. Генерировать, распространять или использовать контент, нарушающий
            законодательство Российской Федерации;</p>
            <p>2.1.2. Генерировать контент, содержащий:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Порнографию, в том числе с участием несовершеннолетних;</li>
              <li>Пропаганду насилия, терроризма, экстремизма;</li>
              <li>Разжигание национальной, расовой или религиозной розни;</li>
              <li>Дискриминацию по любому признаку;</li>
              <li>Призывы к свержению власти или насильственному изменению конституционного строя;</li>
              <li>Инструкции по изготовлению оружия, наркотиков, взрывчатых веществ.</li>
            </ul>
            <p>2.1.3. Использовать Сайт для автоматизированного сбора данных (парсинг, краулинг)
            без письменного разрешения Администрации;</p>
            <p>2.1.4. Пытаться обойти систему аутентификации, тарифные ограничения
            или механизмы защиты Сайта;</p>
            <p>2.1.5. Использовать Сайт для создания контента с целью обмана, введения
            в заблуждение или мошенничества.</p>
            <p>2.1.6. Вводить в поля генерации (промпты, запросы, описания) любые
            персональные данные (свои или третьих лиц), конфиденциальную информацию,
            коммерческую тайну, а также данные, составляющие охраняемую законом тайну.
            Пользователь несёт полную ответственность за содержание вводимых промптов
            и осознаёт, что они передаются сторонним AI-провайдерам для обработки.</p>
          </Section>

          <Section icon={<Shield className="w-5 h-5" />} title="3. Интеллектуальная собственность и контент">
            <p>3.1. Все элементы Сайта (дизайн, код, тексты, графика) являются
            интеллектуальной собственностью Администрации и защищены законом.</p>
            <p>3.2. Контент, сгенерированный с помощью AI-моделей через Сайт (изображения,
            тексты, видео, музыка), создаётся нейросетями третьих сторон.
            Администрация не претендует на авторские права на такой контент
            и не гарантирует его уникальность или отсутствие спорных элементов.
            Пользователь осознаёт, что сгенерированный контент может случайно совпадать
            с произведениями третьих лиц. Администрация не несёт ответственности за такое
            совпадение. Пользователь обязан самостоятельно проверять контент перед
            публичным использованием.</p>
            <p>3.3. Пользователь несёт полную ответственность за контент, который он
            генерирует и распространяет, включая его соответствие законодательству
            и правам третьих лиц.</p>
          </Section>

          <Section icon={<Scale className="w-5 h-5" />} title="4. Ограничение ответственности">
            <p>4.1. Сайт предоставляется на условиях «как есть» (as is). Администрация
            не гарантирует:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Бесперебойную работу Сайта;</li>
              <li>Отсутствие ошибок в работе AI-моделей;</li>
              <li>Точность, полноту или актуальность сгенерированного контента;</li>
              <li>Сохранность данных Пользователя (рекомендуется делать резервные копии).</li>
            </ul>
            <p>4.2. Администрация не несёт ответственности за любые прямые или косвенные
            убытки, возникшие в результате использования или невозможности использования
            Сайта, включая, но не ограничиваясь: потеря данных, упущенная выгода,
            репутационный ущерб.</p>
            <p>4.3. В случае если ответственность Администрации не может быть исключена
            в силу закона, её размер ограничивается суммой, уплаченной Пользователем
            Администрации за последние 30 (тридцать) дней до возникновения претензии.
            Настоящее ограничение не применяется в случаях, когда вред причинён
            умышленно или по грубой неосторожности Администрации, а также в случаях,
            когда ограничение ответственности прямо запрещено законом.</p>
            <p>4.4. Администрация не контролирует и не одобряет контент, генерируемый
            Пользователями. Все претензии третьих лиц к сгенерированному контенту
            предъявляются непосредственно Пользователю.</p>
          </Section>

          <Section icon={<Coins className="w-5 h-5" />} title="5. Токены, платежи и ответственность Пользователя">
            <p>5.1. CN-токены являются внутренней расчётной единицей Сайта.
            Токены не являются электронным средством платежа, не подлежат
            обналичиванию или обратному обмену на деньги.</p>
            <p>5.2. Токены, приобретённые Пользователем за плату, являются авансовым
            платежом. При отказе Пользователя от договора либо при несогласии
            с изменениями Соглашения согласно п. 1.3, стоимость неиспользованных
            токенов подлежит возврату пропорционально остатку, за вычетом фактически
            понесённых Администрацией расходов (включая комиссии платёжных систем).
            Возврат осуществляется по заявлению Пользователя в течение 10 (десяти)
            рабочих дней. Токены, полученные безвозмездно (бонусные, промо-токены),
            возврату и обмену на деньги не подлежат и сгорают при удалении аккаунта
            или блокировке.</p>
            <p>5.3. Администрация вправе изменять стоимость услуг в токенах,
            списывать токены за использование, устанавливать лимиты и бонусы
            в одностороннем порядке.</p>
            <p>5.4. При блокировке аккаунта за грубое нарушение (создание запрещённого
            контента, указанного в п. 2.1.2) Администрация вправе не возвращать
            остаток токенов, если такое нарушение повлекло или могло повлечь ущерб
            третьим лицам или Администрации. В остальных случаях блокировки остаток
            платных токенов подлежит возврату по правилам п. 5.2.</p>
            <p>5.5. Администрация не является платёжным агентом и не принимает
            платежи от третьих лиц. Все расчёты осуществляются через платёжных
            партнёров (ЮKassa и др.).</p>
            <p>5.6. Пользователь обязуется возместить Администрации и её аффилированным лицам
            любые убытки, ущерб, судебные издержки и расходы (включая разумные расходы
            на адвокатов), возникшие в связи с претензиями, исками или требованиями
            третьих лиц, основанными на: (а) контенте, созданном Пользователем;
            (б) нарушении Пользователем условий Соглашения или прав третьих лиц;
            (в) неправомерном использовании Сайта.</p>
          </Section>

          <Section icon={<Shield className="w-5 h-5" />} title="6. Аккаунт и безопасность">
            <p>6.1. Пользователь обязуется хранить пароль в тайне. Администрация
            не несёт ответственности за несанкционированный доступ к аккаунту Пользователя.</p>
            <p>6.2. Пользователь имеет право удалить свой аккаунт в любой момент,
            обратившись в поддержку. При удалении аккаунта платные токены
            возвращаются по заявлению Пользователя в порядке п. 5.2.
            Бонусные токены аннулируются.</p>
            <p>6.3. Администрация вправе заблокировать аккаунт Пользователя
            при нарушении условий Соглашения без предварительного уведомления.</p>
          </Section>

          <Section icon={<Scale className="w-5 h-5" />} title="7. Заключительные положения">
            <p>7.1. По всем вопросам, не урегулированным Соглашением, стороны
            руководствуются законодательством Российской Федерации.</p>
            <p>7.2. Все споры разрешаются в соответствии с законодательством
            Российской Федерации с соблюдением обязательного досудебного
            претензионного порядка. Претензия направляется заказным письмом
            с уведомлением либо на электронный адрес{' '}
            <a href="mailto:support@chat-neuro.ru" className="text-indigo-400 hover:text-indigo-300">
            support@chat-neuro.ru</a>.
            Срок ответа на претензию — 30 (тридцать) календарных дней с момента
            получения. При недостижении согласия спор передаётся в суд в соответствии
            с действующим процессуальным законодательством РФ.</p>
            <p>7.3. Если какое-либо положение Соглашения признаётся недействительным,
            это не влияет на действительность остальных положений.</p>
            <p>7.4. Все претензии и обращения направляются на{' '}
            <a href="mailto:support@chat-neuro.ru" className="text-indigo-400 hover:text-indigo-300">
            support@chat-neuro.ru</a>.</p>
          </Section>

          <Section icon={<AlertTriangle className="w-5 h-5" />} title="8. Уведомления о нарушениях">
            <p>8.1. Если вы считаете, что сгенерированный через Сайт контент нарушает
            ваши права, направьте мотивированное заявление на{' '}
            <a href="mailto:support@chat-neuro.ru" className="text-indigo-400 hover:text-indigo-300">
            support@chat-neuro.ru</a>. Администрация обязуется рассмотреть обращение
            в течение 24 часов и вправе удалить контент без уведомления Пользователя.</p>
            <p>8.2. Пользователь, чей контент удалён, вправе представить возражения.
            При повторных нарушениях аккаунт блокируется.</p>
          </Section>

          <Section icon={<Shield className="w-5 h-5" />} title="9. Реквизиты владельца Сайта">
            <p>9.1. Владелец Сайта Chat-Neuro.ru:</p>
            <div className="text-xs text-gray-400 space-y-1 p-3 rounded-lg bg-white/5 border border-white/10">
              <p className="text-gray-200 font-medium">Григорьев Игорь Валерьевич</p>
              <p>УНП МЕ5805018</p>
              <p>213825, Республика Беларусь, Могилёвская обл., г. Бобруйск, ул. Горелика, 55-11</p>
              <p>E-mail: support@chat-neuro.ru</p>
              <p>Регистрация: 741 Инспекция МНС по г. Бобруйску от 24.07.2024</p>
            </div>
          </Section>
        </div>
      </div>


    </div>
  )
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h2 className="text-base font-bold text-white flex items-center gap-2">
        <span className="text-indigo-400">{icon}</span>
        {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </div>
  )
}
